import { Children, useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import CreateCustomer from './CreateCustomerForm';
import Modal from '../Reusable/Modal';

//@ts-ignore
import { FormLayout } from '@careinfo/layout';
import ConsignmentForm from './ConsignmentForm';
import Button from '../Reusable/Button';
import moment from 'moment';
import APIS from '../../constant/EndPoint';
import { getApiData, postApiData } from '../../helpers/AxiosInstance';

const CreateConsignment = (props: any) => {
  const {
    handleCreate,
    isLoading,
    handleCreateCustomer,
    createCustomerLoading,
    defaultBranch,
    openAddReceiverPopup,
    setOpenAddReceiverPopup,
    openAddSenderPopup,
    setOpenAddSenderPopup,
    openPrintPopup,
    setOpenPrintPopup,
    handleLabelPrintDownload,
    handleConsignmentReceiptDownload,
    isPrintLoading,
    validOrders,
    currentMerchantProfile,
  } = props;
  const {
    control,
    register,
    handleSubmit,
    getValues,
    reset,
    watch,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: async () => {
      return validOrders
        ? {
            is_draft: validOrders?.is_draft,
            pickup_branch: validOrders?.pickup_branch,
            destination: validOrders?.destination,
            cod: validOrders?.cod,
            order_value: validOrders?.order_value,
            service_type: validOrders?.service_type,
            reference_date: moment().format('YYYY-MM-DD'),
            receiver_vat_pan: validOrders?.receiver?.vat_pan,
            reference_type: validOrders?.reference_type,
            reference_number: validOrders?.reference_number,
            note: validOrders?.note,
            delivery_charge: validOrders?.delivery_charge,
            order_items: validOrders?.children?.map((item: any) => ({
              package_type: { ...item?.package_type },
              weight: item?.weight ?? 0,
              piece: item?.piece ?? 0,
              rate: item?.delivery_charge ?? 0,
              remark: item?.reference_number,
              is_rate_by_weight: item?.package_type?.is_rate_by_weight,
              children: item?.children,
            })),
          }
        : {
            pickup_branch: defaultBranch,
            destination: null,
            cod: 0,
            order_value: 0,
            service_type: currentMerchantProfile?.default_service_type,
            reference_date: moment().format('YYYY-MM-DD'),
            receiver_full_name: null,
            receiver_address: null,
            receiver_phone_number: null,
            receiver_vat_pan: null,
            reference_type: null,
            reference_number: null,
            note: '',
            delivery_charge: null,
            order_items: [
              {
                package_type: null,
                weight: 0,
                piece: 1,
                remark: null,
                is_rate_by_weight: false,
              },
            ],
          };
    },
  });
  const [receiverId, setReceiverId] = useState<any>(null);
  const OrderItems = watch('order_items');
  const pickup = watch('pickup_branch');
  const destination = watch('destination');
  const serviceType = watch('service_type');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'order_items',
  });

  const handleClosePrintPopup = () => {
    setOpenPrintPopup(false);
    reset({
      pickup_branch: defaultBranch,
      destination: null,
      cod: 0,
      order_value: 0,
      service_type: currentMerchantProfile?.default_service_type,
      reference_date: moment().format('YYYY-MM-DD'),
      receiver_full_name: getValues('receiver_full_name'),
      receiver_address: getValues('receiver_address'),
      receiver_phone_number: getValues('receiver_phone_number'),
      receiver_vat_pan: getValues('receiver_vat_pan'),
      reference_type: null,
      reference_number: null,
      note: '',
      delivery_charge: null,
      order_items: [
        {
          package_type: null,
          weight: 0,
          piece: 1,
          remark: null,
        },
      ],
    });
  };

  const handleSetCustomerDefaultValue = async (
    pickup_branch: any,
    destination: any
  ) => {
    const url = `${APIS.ORDER}location-wise-customer/`;
    const formData = {
      vendor: currentMerchantProfile?.id,
      pickup_branch: pickup_branch?.id ?? pickup_branch,
      destination: destination?.id ?? destination,
    };
    const res = await postApiData({ url, formData, setError });
    if (res?.status === 200) {
      setValue('receiver_full_name', res?.data?.receiver_name);
      setValue('receiver_address', res?.data?.receiver_address);
      setValue('receiver_phone_number', res?.data?.receiver_primary_contact);
      setValue('receiver_vat_pan', res?.data?.receiver_vat_pan);
    }
  };

  const handleReceiverAutoFillValue = async (receiverId: any) => {
    const url = `${APIS.CUSTOMER}${receiverId}/`;
    const res = await getApiData(url, {});
    if (res.status === 200) {
      setValue('receiver_address', res?.data?.address);
      setValue('receiver_phone_number', res?.data?.primary_contact);
      setValue('receiver_vat_pan', res?.data?.vat_pan);
    }
  };

  useEffect(() => {
    if (validOrders) {
      return;
    }
    const fetchDeliveryCharge = async () => {
      if (!OrderItems || !pickup || !serviceType || !destination) return;
      let total = 0;
      const updatedItems = [];
      for (const item of OrderItems) {
        if (!item?.package_type) return;
        const weight = Number(item?.weight) || 0;
        const piece = Number(item?.piece) || 0;
        const url = `${APIS.ORDER}delivery-charge/`;
        let formData = {
          pickup_branch: pickup?.id ?? pickup,
          service_type: serviceType?.id ?? serviceType,
          product_type: item?.package_type?.id ?? item?.package_type,
          destination: destination?.id ?? destination,
          weight: 1,
        };
        try {
          const res = await postApiData({ url, formData, setError: () => {} });
          if (res.status === 200) {
            const rate = Number(res?.data?.charge) || 0;
            total += item?.is_rate_by_weight ? weight * rate : piece * rate;
            updatedItems.push({
              ...item,
              rate: rate,
            });
          } else {
            updatedItems.push(item);
          }
        } catch (err) {
          console.error(err);
        }
      }
      setValue('delivery_charge', total);
      setValue('order_items', updatedItems);
    };
    fetchDeliveryCharge();
  }, [pickup, serviceType, destination, JSON.stringify(OrderItems)]);

  useEffect(() => {
    if (!pickup || !destination) {
      return;
    }
    setValue('receiver_full_name', null);
    handleSetCustomerDefaultValue(pickup, destination);
  }, [pickup, destination, setValue]);

  useEffect(() => {
    if (!receiverId) return;
    handleReceiverAutoFillValue(receiverId);
  }, [receiverId]);

  return (
    <>
      <FormLayout
        handleSubmit={handleSubmit}
        handleFormSubmit={handleCreate}
        setError={setError}
        buttonLabel={'Create'}
        isLoading={isLoading}
      >
        <ConsignmentForm
          clearErrors={clearErrors}
          errors={errors}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          openAddReceiverPopup={openAddReceiverPopup}
          setOpenAddReceiverPopup={setOpenAddReceiverPopup}
          openAddSenderPopup={openAddSenderPopup}
          setOpenAddSenderPopup={setOpenAddSenderPopup}
          fields={fields}
          append={append}
          remove={remove}
          setReceiverId={setReceiverId}
          control={control}
        />
      </FormLayout>

      <Modal
        isOpen={openAddReceiverPopup}
        setIsOpen={setOpenAddReceiverPopup}
        title={'Create Receiver'}
        children={
          <>
            <CreateCustomer
              handleCreate={handleCreateCustomer}
              isLoading={createCustomerLoading}
              openAddReceiverPopup={openAddReceiverPopup}
              setOpenAddReceiverPopup={setOpenAddReceiverPopup}
            />
          </>
        }
      />

      <Modal
        isOpen={openAddSenderPopup}
        setIsOpen={setOpenAddSenderPopup}
        title={'Create Sender'}
        children={
          <>
            <CreateCustomer
              handleCreate={handleCreateCustomer}
              isLoading={createCustomerLoading}
            />
          </>
        }
      />

      <Modal
        isOpen={openPrintPopup}
        setIsOpen={handleClosePrintPopup}
        title={'Print '}
        children={
          <div>
            <Button
              label={'Print Label'}
              isLoading={isPrintLoading}
              disabled={isPrintLoading}
              onClick={handleLabelPrintDownload}
            />
            <Button
              label={'Print Receipt'}
              isLoading={isPrintLoading}
              disabled={isPrintLoading}
              onClick={handleConsignmentReceiptDownload}
            />
          </div>
        }
      />
    </>
  );
};

export default CreateConsignment;
